/**
 * Watch a camera feed, full size.
 *
 * The hover tooltip shows a snapshot — one frame, and a stale one on a slow
 * camera. Clicking a live feed opens this: the actual HLS stream, playing.
 *
 * Two things make that awkward and are handled here rather than wished away:
 *
 *  1. Only Safari and iOS play an .m3u8 in a plain <video>. Everywhere else
 *     needs Media Source Extensions driven by hls.js — which is 40KB gzipped
 *     that a viewer who never opens a feed should not pay for. So it is a
 *     DYNAMIC import, pulled the first time someone actually watches, and Vite
 *     splits it into its own chunk. Native playback skips it entirely.
 *  2. Not every feed is a video. The CDOT cameras carry an HLS stream; the City
 *     of Denver ones are snapshot only. A snapshot-only feed says so and shows
 *     the still rather than pretending to be video that never loads.
 *
 * The snapshot doubles as the poster, so there is a picture the instant the
 * panel opens while the stream negotiates behind it.
 */

const cacheBust = (url) => {
  const token = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return `${url}${url.includes("?") ? "&" : "?"}_=${token}`;
};

const el = (tag, cls, text) => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
};

export function mountWatch() {
  // Built here rather than in index.html: the whole feature is one import, and
  // nothing else needs to know the panel exists.
  const card = el("aside", "watch-card hud hud--accent");
  card.id = "watch-card";
  card.hidden = true;
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-label", "Live camera feed");

  const head = el("div", "watch-head");
  const title = el("h2", "watch-title");
  const close = el("button", "watch-close");
  close.setAttribute("aria-label", "Close");
  close.textContent = "✕";
  head.append(title, close);

  const stage = el("div", "watch-stage");
  const video = document.createElement("video");
  video.className = "watch-video";
  video.playsInline = true;
  video.muted = true;      // traffic cameras have no sound, and muted is what
  video.controls = true;   // lets it autoplay without a gesture.
  video.autoplay = true;   // declaratively: the browser starts it when the
  video.playsInline = true; // media is ready, rather than us racing play().
  video.hidden = true;
  const still = document.createElement("img");
  still.className = "watch-still";
  still.alt = "";
  still.hidden = true;
  const msg = el("div", "watch-msg");
  msg.hidden = true;
  stage.append(video, still, msg);

  const dirs = el("div", "watch-dirs");
  const rows = document.createElement("dl");
  rows.className = "watch-rows";

  const foot = el("div", "watch-foot");
  const streamLink = el("a", "watch-link");
  streamLink.target = "_blank";
  streamLink.rel = "noopener noreferrer";
  streamLink.textContent = "Open stream ↗";
  const shotLink = el("a", "watch-link");
  shotLink.target = "_blank";
  shotLink.rel = "noopener noreferrer";
  shotLink.textContent = "Open snapshot ↗";
  foot.append(streamLink, shotLink);

  card.append(head, stage, dirs, rows, foot);
  document.body.append(card);

  // Live resources that must be torn down when the panel closes or the view
  // switches: the hls.js engine holds an open connection, and the snapshot-only
  // path runs a timer.
  let hls = null;
  let stillTimer = null;

  function teardownPlayback() {
    if (hls) { hls.destroy(); hls = null; }
    if (stillTimer) { clearInterval(stillTimer); stillTimer = null; }
    video.pause();
    video.removeAttribute("src");
    video.load();
  }

  function setMessage(text) {
    msg.textContent = text ?? "";
    msg.hidden = !text;
  }

  // Calling play() once, imperatively, races the browser's autoplay policy: on
  // Chrome the promise can reject when play() lands after the async import and
  // manifest parse, leaving the poster frozen until the user clicks play
  // themselves. So nudge it whenever the media reports it can play, and again
  // when hls.js has the manifest. Muted playback needs no gesture, so these
  // succeed as soon as something is buffered.
  const nudgePlay = () => { const p = video.play(); if (p) p.catch(() => {}); };
  video.addEventListener("canplay", nudgePlay);
  video.addEventListener("loadeddata", nudgePlay);

  async function playStream(view) {
    video.hidden = false;
    still.hidden = true;
    if (view.url) video.poster = cacheBust(view.url);

    // Safari / iOS play HLS natively; no library, no CORS dance.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = view.stream;
      nudgePlay();
      return;
    }
    // Everyone else: hls.js, loaded on demand.
    try {
      // Plain `const Hls =` rather than destructuring the default, so the
      // imported-constructor test can see it is a real binding.
      const module = await import("hls.js");
      const Hls = module.default;
      if (!Hls.isSupported()) {
        setMessage("This browser can't play the live stream — use “Open stream ↗”.");
        return;
      }
      hls = new Hls({ liveDurationInfinity: true, autoStartLoad: true });
      hls.loadSource(view.stream);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, nudgePlay);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) setMessage("The live stream is unavailable right now.");
      });
    } catch {
      setMessage("Couldn't load the video player — use “Open stream ↗”.");
    }
  }

  function showStill(view) {
    video.hidden = true;
    still.hidden = false;
    const refresh = () => { still.src = cacheBust(view.url); };
    refresh();
    // The snapshot changes every few minutes; a slow refresh keeps the open
    // panel current without hammering the source.
    stillTimer = setInterval(refresh, 30_000);
    setMessage("Snapshot only — this camera has no live video feed.");
  }

  function playView(view) {
    teardownPlayback();
    setMessage("");
    streamLink.hidden = !view.stream;
    if (view.stream) streamLink.href = view.stream;
    shotLink.hidden = !view.url;
    if (view.url) shotLink.href = view.url;

    if (view.stream) playStream(view);
    else if (view.url) showStill(view);
    else setMessage("This feed has no picture.");

    for (const button of dirs.children) {
      button.setAttribute("aria-pressed", String(button._view === view));
    }
  }

  function open(feed) {
    const views = Array.isArray(feed?.views) ? feed.views.filter((v) => v.stream || v.url) : [];
    if (!views.length) return;

    title.textContent = feed.name || "Traffic camera";

    // One button per direction only when there is a real choice to make.
    dirs.replaceChildren();
    dirs.hidden = views.length < 2;
    if (views.length >= 2) {
      views.forEach((view, i) => {
        const label = (view.direction || `View ${i + 1}`)
          .replace(/^.*?(?:at|:)\s*/, "").slice(0, 24) || `View ${i + 1}`;
        const button = el("button", "watch-dir", label);
        button._view = view;
        button.addEventListener("click", () => playView(view));
        dirs.append(button);
      });
    }

    rows.replaceChildren();
    for (const [key, value] of [
      ["Owner", feed.owner],
      ["Route", feed.route],
      ["Location", feed.near && String(feed.near).replace(/^in /, "")],
    ]) {
      if (!value) continue;
      rows.append(el("dt", null, key), el("dd", null, String(value)));
    }

    card.hidden = false;
    playView(views[0]);
    close.focus();
  }

  function hide() {
    teardownPlayback();
    setMessage("");
    card.hidden = true;
  }

  close.addEventListener("click", hide);
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !card.hidden) hide();
  });
  window.addEventListener("pulse:watch-feed", (event) => open(event.detail));

  return { open, hide };
}
